import { Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import React from "react";

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: "Roboto",
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 10,
    padding: 8,
    textAlign: "center",
  },
  table: {
    marginTop: 10,
  },
  tableRow: {
    flexDirection: "row",
    borderBottom: "1pt solid #e0e0e0",
    paddingVertical: 4,
  },
  tableHeader: {
    backgroundColor: "#f5f5f5",
    fontWeight: "bold",
    padding: 6,
  },
  tableCell: {
    padding: 4,
    fontSize: 8,
    flex: 1,
  },
  tableCellStation: {
    padding: 4,
    fontSize: 8,
    flex: 2,
  },
  footer: {
    position: "absolute",
    bottom: 20,
    right: 30,
    fontSize: 8,
    color: "#666",
    marginTop: 30,
  },
});

interface RebiddingTablePDFProps {
  tableRebiddingData: any[];
  tableParticipants: any[];
  proposals?: Record<string, string>;
  maxRebiddingCount: number;
  totalWagonsRequired?: string | number;
  orderNumber?: string | number;
}

export const RebiddingTablePDF: React.FC<RebiddingTablePDFProps> = ({
  tableRebiddingData,
  tableParticipants,
  proposals,
  maxRebiddingCount,
  totalWagonsRequired,
  orderNumber,
}) => {
  if (
    !tableRebiddingData ||
    tableRebiddingData.length === 0 ||
    !tableParticipants ||
    tableParticipants.length === 0
  ) {
    return null;
  }

  // Ограничения на количество отображаемых колонок на одной странице
  const maxParticipantsPerPage = 2; // максимум 2 участника на странице
  const maxRebiddingsPerPage = 3; // максимум 3 переторжки на странице (начальное + 3 переторжки = 4 колонки)
  const totalColsPerParticipant = maxRebiddingCount + 1; // начальное + переторжки

  // Разбиваем участников на группы по maxParticipantsPerPage
  const participantGroups: any[][] = [];
  for (let i = 0; i < tableParticipants.length; i += maxParticipantsPerPage) {
    participantGroups.push(tableParticipants.slice(i, i + maxParticipantsPerPage));
  }

  // Разбиваем переторжки на группы: начальное (0) всегда + до 3 переторжек на странице
  const rebiddingGroups: number[][] = [];
  
  // Первая страница: начальное (0) + первые 3 переторжки (1, 2, 3)
  const firstGroup: number[] = [0];
  for (let col = 1; col < Math.min(4, totalColsPerParticipant); col++) {
    firstGroup.push(col);
  }
  rebiddingGroups.push(firstGroup);
  
  // Последующие страницы: начальное (0) + следующие группы по 3 переторжки
  for (let startRebid = 4; startRebid < totalColsPerParticipant; startRebid += maxRebiddingsPerPage) {
    const endRebid = Math.min(startRebid + maxRebiddingsPerPage, totalColsPerParticipant);
    const group: number[] = [0]; // Начальное предложение всегда включено
    for (let col = startRebid; col < endRebid; col++) {
      group.push(col);
    }
    rebiddingGroups.push(group);
  }

  // Генерируем страницы для каждой комбинации группы участников и группы переторжек
  const pages: JSX.Element[] = [];
  participantGroups.forEach((participantGroup, groupIndex) => {
    rebiddingGroups.forEach((rebiddingGroup, rebiddingGroupIndex) => {
      const pageKey = `group-${groupIndex}-rebidding-${rebiddingGroupIndex}`;

      pages.push(
        <Page
          key={pageKey}
          size="A4"
          orientation="landscape"
          style={styles.page}
        >
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              Сводная таблица по итогам, заявка №{orderNumber || ""}
              {participantGroups.length > 1 || rebiddingGroups.length > 1
                ? ` (стр. ${groupIndex * rebiddingGroups.length + rebiddingGroupIndex + 1} из ${participantGroups.length * rebiddingGroups.length})`
                : ""}
            </Text>
            <View style={styles.table}>
              {/* Заголовок таблицы - первая строка */}
              <View style={[styles.tableRow, styles.tableHeader]}>
                <Text style={styles.tableCellStation}>Дорога/станция выгрузки</Text>
                <Text style={styles.tableCell}>Предложение грузовладельца</Text>
                {participantGroup.map((participant) =>
                  rebiddingGroup.map((colIndex) => (
                    <Text key={`header-${participant.id}-${colIndex}`} style={styles.tableCell}>
                      {colIndex === 0 ? participant.name : ""}
                    </Text>
                  ))
                )}
                <Text style={styles.tableCell}>Тип тарифа</Text>
              </View>
              {/* Заголовок таблицы - вторая строка */}
              <View style={[styles.tableRow, styles.tableHeader]}>
                <Text style={styles.tableCellStation}></Text>
                <Text style={styles.tableCell}></Text>
                {participantGroup.map((participant) =>
                  rebiddingGroup.map((colIndex) => (
                    <Text key={`subheader-${participant.id}-${colIndex}`} style={styles.tableCell}>
                      {colIndex === 0 ? "начальное" : `переторжка ${colIndex}`}
                    </Text>
                  ))
                )}
                <Text style={styles.tableCell}></Text>
              </View>

              {/* Данные таблицы */}
              {tableRebiddingData.map((row) => {
                return (
                  <View key={row.id} style={styles.tableRow}>
                    <Text style={styles.tableCellStation}>{row.roadStation || "-"}</Text>
                    <Text style={styles.tableCell}>
                      {proposals?.[row.id] || row.yourProposal || "-"}
                    </Text>
                    {participantGroup.map((participant) => {
                      const bid = participant.bids[row.id];
                      return rebiddingGroup.map((colIndex) => (
                        <Text key={`cell-${participant.id}-${row.id}-${colIndex}`} style={styles.tableCell}>
                          {colIndex === 0
                            ? bid?.initial || "-"
                            : bid?.rebiddings?.[colIndex] || "-"}
                        </Text>
                      ));
                    })}
                    <Text style={styles.tableCell}>{row.unit}</Text>
                  </View>
                );
              })}

              {/* Строка "Итого вагонов" */}
              <View style={[styles.tableRow, styles.tableHeader]}>
                <Text style={styles.tableCellStation}>
                  <Text style={{ fontWeight: "bold" }}>Итого вагонов</Text>
                </Text>
                <Text style={styles.tableCell}>
                  <Text style={{ fontWeight: "bold" }}>{totalWagonsRequired ?? "-"}</Text>
                </Text>
                {participantGroup.map((participant) =>
                  rebiddingGroup.map((colIndex) => (
                    <Text key={`total-${participant.id}-${colIndex}`} style={styles.tableCell}>
                      {colIndex === 0 ? (
                        <Text style={{ fontWeight: "bold" }}>
                          {participant.countWagonsOwner ?? ""}
                        </Text>
                      ) : (
                        ""
                      )}
                    </Text>
                  ))
                )}
                <Text style={styles.tableCell}>
                  <Text style={{ fontWeight: "bold" }}>шт.</Text>
                </Text>
              </View>
            </View>
          </View>
          {orderNumber && (
            <Text
              style={styles.footer}
              fixed
              render={({ pageNumber }) =>
                `Заявка ${orderNumber} Лист ${pageNumber}`
              }
            />
          )}
        </Page>
      );
    });
  });

  return <>{pages}</>;
};
